<template>
  <div class="fare-cell" :class="stateClass">
    <input
      type="number"
      min="0"
      step="0.01"
      class="custom-form-control fare-input"
      :class="{ empty: isEmpty }"
      :value="display"
      placeholder="—"
      :disabled="saving"
      @focus="onFocus"
      @blur="onBlur"
      @input="local = $event.target.value"
    />
    <div class="fare-meta">
      <span class="client">{{ clientLabel }}</span>
      <span v-if="cell.margin != null" class="margin" :class="marginClass">
        {{ marginLabel }}
      </span>
    </div>
  </div>
</template>

<script>
import { ProviderService } from "@/core/services";

// Celda del tarifario del proveedor (DEF-ADM-020 §4.1). Debajo del monto editable
// muestra la tarifa de venta al cliente y el margen: verde si es positivo, rojo si
// es cero o negativo. Guarda al salir del campo, como el tarifario de clientes.
export default {
  name: "ProviderFareCell",
  props: {
    providerId: { type: [String, Number], required: true },
    districtId: { type: String, required: true },
    routeType: { type: String, required: true },
    cell: { type: Object, required: true }, // { vehicle_type, provider_amount, client_amount, margin }
  },
  data() {
    return {
      local: this.cell.provider_amount != null ? String(this.cell.provider_amount) : "",
      state: "idle", // idle | ok | error
      saving: false,
      focused: false,
    };
  },
  computed: {
    isEmpty() {
      return this.cell.provider_amount == null;
    },
    display() {
      if (this.focused) return this.local;
      return this.cell.provider_amount != null ? String(this.cell.provider_amount) : "";
    },
    clientLabel() {
      return this.cell.client_amount != null ? `Cliente ${this.cell.client_amount}` : "Sin tarifa cliente";
    },
    marginLabel() {
      const pct = this.cell.margin_percent != null ? ` (${this.cell.margin_percent.toFixed(0)}%)` : "";
      return `${this.cell.margin > 0 ? "+" : ""}${this.cell.margin}${pct}`;
    },
    marginClass() {
      return this.cell.margin > 0 ? "margin-ok" : "margin-bad";
    },
    stateClass() {
      if (this.state === "ok") return "flash-ok";
      if (this.state === "error") return "flash-error";
      return "";
    },
  },
  watch: {
    "cell.provider_amount"(v) {
      this.local = v != null ? String(v) : "";
    },
  },
  methods: {
    onFocus() {
      this.focused = true;
      this.local = this.cell.provider_amount != null ? String(this.cell.provider_amount) : "";
    },
    async onBlur() {
      this.focused = false;
      const raw = (this.local || "").trim();

      // Campo vacío: borra la tarifa si la había; si no, no hay nada que hacer.
      if (raw === "") {
        if (this.cell.provider_amount == null) return;
        await this.save(null);
        return;
      }

      const num = parseFloat(raw);
      if (isNaN(num) || num < 0) {
        this.local = this.cell.provider_amount != null ? String(this.cell.provider_amount) : "";
        return;
      }
      if (num === this.cell.provider_amount) return;

      await this.save(num);
    },
    async save(amount) {
      this.saving = true;
      try {
        await ProviderService.saveFares(this.providerId, [
          {
            district_id: this.districtId,
            route_type: this.routeType,
            vehicle_type: this.cell.vehicle_type,
            amount,
          },
        ]);
        this.flash("ok");
        this.$emit("saved", {
          districtId: this.districtId,
          vehicleType: this.cell.vehicle_type,
          amount,
        });
      } catch (e) {
        console.error(e);
        this.flash("error");
        this.local = this.cell.provider_amount != null ? String(this.cell.provider_amount) : "";
      } finally {
        this.saving = false;
      }
    },
    flash(kind) {
      this.state = kind;
      if (this._t) clearTimeout(this._t);
      this._t = setTimeout(() => {
        this.state = "idle";
      }, 1000);
    },
  },
  beforeDestroy() {
    if (this._t) clearTimeout(this._t);
  },
};
</script>

<style lang="stylus" scoped>
.fare-cell
  border-radius 6px
  padding 2px
  transition background-color .3s ease
// El tamaño y el borde los pone custom-form-control (wize-admin); aquí solo
// lo que la rejilla de tarifas necesita.
.fare-input
  min-width 90px
  text-align right
  background-color #fff
  &.empty::placeholder
    color #adb5bd
    opacity 1
.fare-meta
  display flex
  justify-content space-between
  gap 6px
  font-size 11px
  padding 2px 2px 0
.client
  color #6c757d
.margin
  font-weight 600
.margin-ok
  color #28a745
.margin-bad
  color #dc3545
.flash-ok
  background-color rgba(37,194,110,.25)
.flash-error
  background-color rgba(220,53,69,.25)
</style>
